import type {Pay4MeService, Pay4MeCampaignResponse} from '../../../infrastructure/services/Pay4MeService';
import type {IOrderRepository} from '../../../domain/repositories/IOrderRepository';
import type { Order } from '../../../domain/entities/Order';

type Pay4MeEligibilityOrder = Order & {
    status?: string | null;
    pay4me_campaign_id?: string | null;
};

export interface CreatePay4MeCampaignParams {
    orderId: string;
    title: string;
    description: string;
    durationDays: number;
    userId: string;
}

export class CreatePay4MeCampaignUseCase {
    constructor(
        private readonly pay4meService: Pay4MeService,
        private readonly orderRepository: IOrderRepository
    ) {
    }

    async execute(params: CreatePay4MeCampaignParams): Promise<Pay4MeCampaignResponse> {
        // 1. Validate order exists and belongs to user
        const order = await this.orderRepository.findById(params.orderId);
        if (!order) {
            throw new Error('Order not found');
        }

        // 2. Validate order is eligible for Pay4Me
        this.validateOrderEligibility(order);

        // 3. Validate campaign parameters
        this.validateCampaignParams(params);

        // 4. Create Pay4Me campaign
        const campaign = await this.pay4meService.createCampaign(params.orderId, {
            title: params.title,
            description: params.description,
            durationDays: params.durationDays
        });

        return campaign;
    }

    private validateOrderEligibility(order: Pay4MeEligibilityOrder): void {
        // Check if order has a payment plan (PSS/PaySmallSmall)
        // Backend API returns `payment_method` with values: 'direct_payment' or 'paysmallsmall'
        // Only 'paysmallsmall' orders are eligible for Pay4Me
        const paymentMethod = String(order.payment_method || '')
            .trim()
            .toLowerCase()
            .replace(/[\s-]+/g, '_');
        const isPaySmallSmall = paymentMethod === 'paysmallsmall' ||
                                paymentMethod === 'pay_small_small' ||
                                paymentMethod === 'pss' ||
                                paymentMethod === 'installment' ||
                                paymentMethod === 'pay_later';

        if (!isPaySmallSmall) {
            throw new Error('Only orders with payment plans are eligible for Pay4Me');
        }

        // Check if order is not cancelled or refunded
        // Backend uses UPPERCASE status values: 'CANCELLED', 'REFUNDED'
        const status = (order.status || '').toUpperCase();
        if (status === 'CANCELLED' || status === 'REFUNDED') {
            throw new Error('Cannot create campaign for cancelled or refunded orders');
        }

        // Check if order already has an active campaign
        // API returns pay4me_campaign_id (snake_case)
        if (order.pay4me_campaign_id) {
            throw new Error('Order already has an active Pay4Me campaign');
        }
    }

    private validateCampaignParams(params: CreatePay4MeCampaignParams): void {
        // Validate title: optional but if provided, 5-160 chars (backend limit)
        if (params.title && params.title.trim().length > 0 && params.title.trim().length < 5) {
            throw new Error('Campaign title must be at least 5 characters long');
        }

        if (params.title && params.title.length > 160) {
            throw new Error('Campaign title cannot exceed 160 characters');
        }

        // Validate description: optional but if provided, 10-280 chars (backend limit)
        if (params.description && params.description.trim().length > 0 && params.description.trim().length < 10) {
            throw new Error('Campaign description must be at least 10 characters long');
        }

        if (params.description && params.description.length > 280) {
            throw new Error('Campaign description cannot exceed 280 characters');
        }

        // Validate duration
        if (params.durationDays < 1 || params.durationDays > 90) {
            throw new Error('Campaign duration must be between 1 and 90 days');
        }
    }
}
